/**
 * Read-only DrawingObjectGraph facade (PR2, plan §13).
 *
 * A thin, read-only relationship layer over the existing per-document
 * `DocumentMemoryHolder` (compact store + idIndex + NAME-keyed symbol records)
 * and the new `dependencyIndex`. INDEX-OVER-EXISTING (D5): NOTHING is allocated
 * here beyond the entity the compact store already materialises on a lookup;
 * there is no class-per-entity and the 685k-entity compact store is untouched.
 *
 * Like `EntityMetadataIndexFacade` (mutationStore.ts) the exported singleton
 * delegates to the ACTIVE document via the mutationStore accessors, so a
 * reference captured once auto-retargets when `setActiveDocument` flips the
 * pointer.
 *
 * `getEntity` / `getEntityByHandle` are O(1) via the holder idIndex (NO linear
 * scan). `getLayer` / `getBlockDefinition` read the active document's NAME-keyed
 * records case-insensitively — the document is threaded through a provider the
 * editorStore registers (avoids a `drawingObjectGraph → editorStore` import
 * cycle). `getDependents` delegates to the holder dependency index.
 *
 * Source of truth: docs/superpowers/plans/2026-05-30-drawing-object-graph.md §13.
 */

import type {
  BlockDefinition,
  DependencyRefKind,
  DrawingDocument,
  DrawingEntity,
  EntityRef,
  LayerDefinition,
} from "@dxf-ai-editor/drawing-model";

import {
  getActiveDocumentId,
  getEntityFromCompactCache,
  peekDocumentMemoryHolder,
} from "./mutationStore";

export interface DrawingObjectGraph {
  /** O(1) entity lookup by id via the holder idIndex (NOT a linear scan). */
  getEntity(id: string): DrawingEntity | undefined;
  /**
   * Entity by DXF handle. Regular space entities carry NO `handle` in the
   * model (`drawingEntityBaseSchema` has only `id`); handles live on
   * blocks/blockRecords/layouts/attdef. The id-keyed compact store therefore
   * cannot resolve a space-entity handle — this returns `undefined` for them.
   * Kept on the interface for the object-model contract; a handle→id side index
   * is intentionally NOT added (D5: ONE reverse index only).
   */
  getEntityByHandle(handle: string): DrawingEntity | undefined;
  /** Layer record by name, case-insensitive, off the active document. */
  getLayer(name: string): LayerDefinition | undefined;
  /** Block definition by name, case-insensitive, off the active document. */
  getBlockDefinition(name: string): BlockDefinition | undefined;
  /** Entities referencing `name` for `refKind`, via the dependency index. */
  getDependents(
    refKind: DependencyRefKind,
    name: string,
  ): readonly EntityRef[];
}

/**
 * Active-document provider. The geometry document lives in the Zustand
 * `editorStore`; importing it here would create a cycle (editorStore →
 * mutationStore → … and back), so the editorStore registers a getter at init.
 * Defaults to `undefined` (no document) until registered — `getLayer` /
 * `getBlockDefinition` then return `undefined`, which is correct for a graph
 * with no open document.
 */
let documentProvider: () => DrawingDocument | undefined = () => undefined;

/**
 * Register the active-document accessor (called once by the editorStore). Idempotent.
 */
export function setDrawingObjectGraphDocumentProvider(
  provider: () => DrawingDocument | undefined,
): void {
  documentProvider = provider;
}

const EMPTY_DEPENDENTS: readonly EntityRef[] = Object.freeze([]);

class DrawingObjectGraphFacade implements DrawingObjectGraph {
  getEntity(id: string): DrawingEntity | undefined {
    const entity = getEntityFromCompactCache(id);
    // The compact cache can return an AttdefEntity (block child); narrow to the
    // DrawingEntity union — Attdef is not part of `DrawingEntity` so callers
    // that asked for a space entity never receive one.
    if (entity === undefined) return undefined;
    return entity.type === "Attdef"
      ? undefined
      : (entity as DrawingEntity);
  }

  getEntityByHandle(_handle: string): DrawingEntity | undefined {
    // Space entities carry no handle (see interface JSDoc). No handle index.
    return undefined;
  }

  getLayer(name: string): LayerDefinition | undefined {
    const doc = documentProvider();
    if (doc === undefined) return undefined;
    const lc = name.toLowerCase();
    return doc.styleTables.layers.find((l) => l.name.toLowerCase() === lc);
  }

  getBlockDefinition(name: string): BlockDefinition | undefined {
    const doc = documentProvider();
    if (doc === undefined) return undefined;
    // `blocks` is a name-keyed Record. Try the exact key first (the common
    // case), then a case-insensitive scan (AutoCAD names are case-insensitive).
    const exact = doc.blocks[name];
    if (exact !== undefined) return exact;
    const lc = name.toLowerCase();
    for (const [key, def] of Object.entries(doc.blocks)) {
      if (key.toLowerCase() === lc) return def;
    }
    return undefined;
  }

  getDependents(
    refKind: DependencyRefKind,
    name: string,
  ): readonly EntityRef[] {
    const holder = peekDocumentMemoryHolder(getActiveDocumentId());
    if (holder === undefined) return EMPTY_DEPENDENTS;
    return holder.dependencyIndex.getDependents(refKind, name);
  }
}

/**
 * The shared read-only object-graph singleton. Delegates to the active document
 * exactly like `entityMetadataIndex` — captured references auto-retarget on
 * `setActiveDocument`.
 */
export const drawingObjectGraph: DrawingObjectGraph =
  new DrawingObjectGraphFacade();
